import * as React from "react";
import { useState } from "react";
import {
  List,
  Datagrid,
  TextField,
  NumberField,
  BooleanField,
  FunctionField,
  useDataProvider,
  useNotify,
  useRefresh,
} from "react-admin";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField as MuiTextField,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useClub } from "../club/ClubContext";
import { ActivateChallengeTemplateButton } from "./ActivateChallengeTemplateButton";

function ruleLabel(rule?: string) {
  if (!rule) return "—";

  const periodSuffix = rule.endsWith("_per_week")
    ? " (Woche)"
    : rule.endsWith("_per_month")
      ? " (Monat)"
      : rule.endsWith("_per_year")
        ? " (Jahr)"
        : "";

  const base = rule
    .replace("_per_week", "")
    .replace("_per_month", "")
    .replace("_per_year", "");

  switch (base) {
    case "trainings_completed":
      return `Trainings${periodSuffix || " (einmalig/window)"}`;
    case "numeric_games_completed":
      return `Numeric Games${periodSuffix || " (einmalig/window)"}`;
    case "numeric_score_hits":
      return `Numeric Score >= X${periodSuffix}`;
    case "numeric_value_hits":
      return `Numeric Value Hits${periodSuffix}`;
    default:
      return rule;
  }
}

function todayIso() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isPeriodicRule(rule?: string) {
  return !!rule && (rule.endsWith("_per_week") || rule.endsWith("_per_month") || rule.endsWith("_per_year"));
}
function needsMatchScore(rule?: string) {
  return !!rule && rule.startsWith("numeric_score_hits");
}
function needsValueScore(rule?: string) {
  return !!rule && rule.startsWith("numeric_value_hits");
}

export function ChallengeTemplateList(props: any) {
  const { clubId } = useClub();
  const dp = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState<any | null>(null);

  // form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [target, setTarget] = useState<number>(5);
  const [windowDays, setWindowDays] = useState<number | "">("");
  const [reward, setReward] = useState<number>(100);
  const [startsOn, setStartsOn] = useState<string>(todayIso());
  const [endsOn, setEndsOn] = useState<string>("");
  const [isActive, setIsActive] = useState(true);

  // params
  const [matchScore, setMatchScore] = useState<number | "">("");
  const [valueScore, setValueScore] = useState<number | "">("");

  const openDialog = (record: any) => {
    setTpl(record);
    setName(String(record?.name ?? ""));
    setDescription(record?.description ?? "");

    // eure Spalten heißen target/window_days/default_reward_points
    setTarget(Number(record?.target ?? 5));

    const periodic = isPeriodicRule(record?.rule_type);
    setWindowDays(periodic ? "" : (record?.window_days ?? ""));

    setReward(Number(record?.default_reward_points ?? 100));

    setMatchScore(record?.params?.match_score ?? "");
    setValueScore(record?.params?.score ?? "");

    setStartsOn(todayIso());
    setEndsOn("");
    setIsActive(true);
    setOpen(true);
  };

  const activate = async () => {
    if (!clubId || !tpl?.id) {
      notify("Verein oder Template fehlt", { type: "warning" });
      return;
    }

    const rt = String(tpl?.rule_type ?? "");
    const periodic = isPeriodicRule(rt);

    const params: any = {};
    if (needsMatchScore(rt)) {
      params.match_score = matchScore === "" ? null : Number(matchScore);
    }
    if (needsValueScore(rt)) {
      params.score = valueScore === "" ? null : Number(valueScore);
    }

    try {
      await dp.custom("challenges/from-template", {
        method: "POST",
        body: {
          club_id: clubId,
          template_id: tpl.id,
          overrides: {
            name,
            description,
            target: Number(target),
            window_days: periodic ? null : (windowDays === "" ? null : Number(windowDays)),
            reward_points: Number(reward),
            starts_on: startsOn || null,
            ends_on: endsOn || null,
            is_active: !!isActive,
            params: Object.keys(params).length ? params : undefined,
          },
        },
      });

      notify("Challenge aktiviert", { type: "info" });
      setOpen(false);
      refresh();
    } catch (e: any) {
      notify("Aktivieren fehlgeschlagen", { type: "warning" });
    }
  };

  return (
    <>
      <List {...props} perPage={50} sort={{ field: "id", order: "DESC" }}>
        <Datagrid>
          <TextField source="key" />
          <TextField source="name" />
          <FunctionField label="Regel" render={(rec: any) => ruleLabel(rec?.rule_type)} />
          <NumberField source="target" label="Default Ziel" />
          <NumberField source="window_days" label="Default Fenster" />
          <NumberField source="default_reward_points" label="Default Reward" />
          <BooleanField source="is_enabled" label="Enabled" />

          <ActivateChallengeTemplateButton onClick={openDialog} />
        </Datagrid>
      </List>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Challenge aktivieren</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <MuiTextField
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <MuiTextField
              label="Beschreibung"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />

            <MuiTextField
              label={`Regel (${ruleLabel(tpl?.rule_type ?? "")})`}
              value={tpl?.rule_type ?? ""}
              disabled
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <MuiTextField
                label="Ziel"
                type="number"
                value={target}
                onChange={(e) => setTarget(Number(e.target.value))}
                fullWidth
              />

              <MuiTextField
                label="Fenster (Tage)"
                type="number"
                value={windowDays}
                onChange={(e) =>
                  setWindowDays(e.target.value === "" ? "" : Number(e.target.value))
                }
                helperText={
                  isPeriodicRule(tpl?.rule_type)
                    ? "Bei *_per_week/_per_month/_per_year ist das Kalender-Intervall fix."
                    : "Optional (rollierend)"
                }
                disabled={isPeriodicRule(tpl?.rule_type)}
                fullWidth
              />
            </Stack>

            {needsMatchScore(tpl?.rule_type) && (
              <MuiTextField
                label="match_score (Score >= X)"
                type="number"
                value={matchScore}
                onChange={(e) =>
                  setMatchScore(e.target.value === "" ? "" : Number(e.target.value))
                }
                helperText="Zählt Numeric-Trainings mit Score >= match_score"
                fullWidth
              />
            )}

            {needsValueScore(tpl?.rule_type) && (
              <MuiTextField
                label="score (Wert, z. B. 180)"
                type="number"
                value={valueScore}
                onChange={(e) =>
                  setValueScore(e.target.value === "" ? "" : Number(e.target.value))
                }
                helperText="Summiert Treffer dieses Werts aus numeric_score_hits"
                fullWidth
              />
            )}

            <MuiTextField
              label="Reward Points"
              type="number"
              value={reward}
              onChange={(e) => setReward(Number(e.target.value))}
              fullWidth
            />

            <Stack direction="row" spacing={2}>
              <MuiTextField
                label="Start"
                type="date"
                value={startsOn}
                onChange={(e) => setStartsOn(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <MuiTextField
                label="Ende"
                type="date"
                value={endsOn}
                onChange={(e) => setEndsOn(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Optional"
                fullWidth
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label="Sofort aktiv"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Abbrechen</Button>
          <Button onClick={activate} variant="contained" disabled={!clubId || !tpl?.id}>
            Aktivieren
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
